import {
  OVERLAY,
  SELECT_PROJECT_ADD_TO,
  SELECT_PROJECT_SUFFIX,
  EVENT_NAME_PREFIX_PROJECTS,
  STASH,
  MY_ACCOUNT_SETTINGS,
  BUTTON_SUBMISSION,
  INTERACTION,
  CREATE_LIST,
  ADD_TO_MOBILE,
  ADD_TO_PREFIX,
  ADD_TO_SCREEN,
  LIST_BUILDER_OVERLAY_PREVIEW_LIST,
  MY_LIST_SHARED,
} from '../constants';
import { getPageType } from '../thd-analytics-utils';

const getListScreenLocation = (screen, { pageOnly = false } = {}) => {
  // Handle null/undefined screen values
  if (!screen) {
    screen = 'DEFAULT';
  }

  let page = getPageType();
  if (page === 'browse' || page === 'search') page = 'plp';
  const isUnknownPage = !page || page === 'unknown';
  const screenText = screen.toLowerCase().split('_').join(' ');

  // Determine final location string
  let listLocation;
  if (page === MY_LIST_SHARED) {
    // Keep shared list location as-is
    listLocation = 'lists: shared list';
  } else if (isUnknownPage) {
    // No prefix for unknown pages, just use screen mapping or location as-is
    listLocation = ADD_TO_SCREEN[screen] || screenText;
  } else {
    // Use page prefix if available, otherwise fall back to page type
    const prefix = ADD_TO_PREFIX[page] || page;
    if (pageOnly) {
      listLocation = prefix;
    } else {
      const screenLocation = ADD_TO_SCREEN[screen] || screenText;
      listLocation = `${prefix}: ${screenLocation}`;
    }
  }

  return listLocation;
};

export const addTo = () => {
  // 7.51.2 Create New Project button click
  LIFE_CYCLE_EVENT_BUS.on(SELECT_PROJECT_ADD_TO, () => {
    let page = getPageType();
    if (page === 'browse' || page === 'search') page = 'plp';
    const newPage = EVENT_NAME_PREFIX_PROJECTS[page] ?? page;
    const currentEventName = `${newPage}${SELECT_PROJECT_SUFFIX}`;
    const ddoEvent = {
      category: {
        primaryCategory: OVERLAY
      },
      eventInfo: {
        eventName: currentEventName
      }
    };

    digitalData.event = digitalData.event || [];
    digitalData.event.push(ddoEvent);
  });

  LIFE_CYCLE_EVENT_BUS.on('add-to.addTo', () => {
    const ddoEvent = {
      category: {
        primaryCategory: OVERLAY
      },
      eventInfo: {
        eventName: 'add to'
      }
    };

    digitalData.event = digitalData.event || [];
    digitalData.event.push(ddoEvent);
  });

  // 6.4 Add Materials/Start Shopping Buttons
  window.LIFE_CYCLE_EVENT_BUS.on('add-to.addToMaterials', ({ output }) => {
    const buttonType = output?.buttonType || '';
    const ddoEvent = {
      category: {
        primaryCategory: STASH
      },
      eventInfo: {
        eventName: MY_ACCOUNT_SETTINGS
      },
      eventMode: STASH,
      stash: {
        myAccount: {
          settings: `lists: ${buttonType}`
        }
      }
    };

    digitalData.event = digitalData.event || [];
    digitalData.event.push(ddoEvent);
  });

  // 6.6 Add to List Overlay (Mini PLP)
  window.LIFE_CYCLE_EVENT_BUS.on('add-to.addToMaterialsOverlay', ({ output }) => {
    const addType = output?.addType || '';
    const aiPromptLocation = output?.aiPromptLocation || 'none';
    const ddoEvent = {
      category: {
        primaryCategory: OVERLAY
      },
      eventInfo: {
        eventName: `add to ${addType}`
      },
      myList: {
        aiPromptLocation: aiPromptLocation,        
      }
    }; 

    digitalData.event = digitalData.event || [];
    digitalData.event.push(ddoEvent);
  });

 //Remove non-lifeCycle one once everything is confirmed in PROD.  This allows backwards compatibility during transition
  window.LIFE_CYCLE_EVENT_BUS.lifeCycle.on('add-to.addToMaterialsOverlay', ({ output }) => {
    const addType = output?.addType || '';
    const aiPromptLocation = output?.aiPromptLocation || 'none';
    const ddoEvent = {
      category: {
        primaryCategory: OVERLAY
      },
      eventInfo: {
        eventName: `add to ${addType}`
      },
      myList: {
        aiPromptLocation: aiPromptLocation,        
      }
    }; 
       
    digitalData.event = digitalData.event || [];
    digitalData.event.push(ddoEvent);
  });

  // 6.10.2 updated from older 6.9.2 New List Created w/Product Add
  window.LIFE_CYCLE_EVENT_BUS.on('add-to.newListCreatedProductAdded', ({ output }) => {
    const {
      location,
      items,
      listTotal,
      uniqueSkuCount,
      quantityTotal
    } = output || {};

    if (!items || !Array.isArray(items)) {
      return;
    }

    const itemsFormatted = items.map((item) => ({
      quantity: item.quantity,
      listLocation: location,
      price: {
        basePrice: item.price * item.quantity
      },
      productInfo: {
        sku: item.sku,
        intent: {
          status: item.intentStatus || 'no',
          resolvedIntent: item.resolvedFromIntent || 'no'
        },
        aiSessionId: item.aiSessionId || '',
        listStarter: item.listStarter || ''
      }
    }));

    const ddoEvent = {
      category: {
        primaryCategory: INTERACTION
      },
      eventInfo: {
        eventName: CREATE_LIST
      },
      myList: {
        creationLocation: location
      },
      item: itemsFormatted
    };

    // Add list-level metrics if provided (6.10.2 requirement)
    if (listTotal !== undefined || uniqueSkuCount !== undefined || quantityTotal !== undefined) {
      ddoEvent.myList = {
        ...ddoEvent.myList,
        listTotal: listTotal ?? '',
        uniqueSkuCount: uniqueSkuCount ?? '',
        quantityTotal: quantityTotal ?? ''
      };
    }

    digitalData.event = digitalData.event || [];
    digitalData.event.push(ddoEvent);
  });

  // 6.12.1 Add to List Action
  window.LIFE_CYCLE_EVENT_BUS.on('add-to.addToAction', ({ output }) => {
    const { name, location, items, isBulkAction } = output;
    const itemsFormatted = items.map((item) => ({
      quantity: item.quantity,
      listLocation: location,
      price: {
        basePrice: item.price * item.quantity
      },
      productInfo: {
        sku: item.sku
      }
    }));
    const ddoEvent = {
      category: {
        primaryCategory: BUTTON_SUBMISSION
      },
      eventInfo: {
        eventName: name
      },
      bulkAction: isBulkAction,
      item: itemsFormatted
    };

    digitalData.event = digitalData.event || [];
    digitalData.event.push(ddoEvent);
  });

  // 6.6.1 Mini PLP Search
  window.LIFE_CYCLE_EVENT_BUS.on('add-to.miniPLPSearch', ({ output }) => {
    const searchTerm = output?.searchTerm || '';
    const ddoEvent = {
      category: {
        primaryCategory: BUTTON_SUBMISSION
      },
      eventInfo: {
        eventName: 'mini plp search'
      },
      miniPLPSearchTerm: searchTerm
    };

    digitalData.event = digitalData.event || [];
    digitalData.event.push(ddoEvent);
  });

  // 6.6.3 Add to List/Add Items to List Overlay: Back/X/OK/Close
  window.LIFE_CYCLE_EVENT_BUS.on('add-to.addToOverlayClose', ({ output }) => {
    const actionType = output?.actionType || '';
    const ddoEvent = {
      category: {
        primaryCategory: OVERLAY
      },
      eventInfo: {
        eventName: actionType || 'add to overlay close'
      }
    };

    digitalData.event = digitalData.event || [];
    digitalData.event.push(ddoEvent);
  });

  // 6.7 Add Items to List Overlay (Mini PIP)
  window.LIFE_CYCLE_EVENT_BUS.on('add-to.addToSearchPipView', ({ output }) => {
    const addToType = output?.addToType || '';
    const ddoEvent = {
      category: {
        primaryCategory: OVERLAY
      },
      eventInfo: {
        eventName: `add items to ${addToType}`
      }
    };

    digitalData.event = digitalData.event || [];
    digitalData.event.push(ddoEvent);
  });

  LIFE_CYCLE_EVENT_BUS.on(ADD_TO_MOBILE, () => {
    const ddoEvent = {
      category: {
        primaryCategory: OVERLAY
      },
      eventInfo: {
        eventName: 'add to'
      }
    };

    digitalData.event = digitalData.event || [];
    digitalData.event.push(ddoEvent);
  });

  // 6.12.2 Post-Page Error Tracking for AI
  // Track AI-specific errors like timeout and generic errors
  window.LIFE_CYCLE_EVENT_BUS.on('list-builder.error', ({ output }) => {
    const errorType = output?.errorType || 'system';
    const errorMessage = output?.errorMessage || '';
    const errorCode = output?.errorCode || 'n/a';
    const pageSection = output?.pageSection || 'add to list overlay';

    const ddoEvent = {
      category: {
        primaryCategory: 'error'
      },
      eventInfo: {
        eventName: 'post page error',
        error: [{
          type: errorType,
          displayType: 'inline',
          pageSection,
          message: errorMessage,
          code: errorCode
        }]
      }
    };

    digitalData.event = digitalData.event || [];
    digitalData.event.push(ddoEvent);
  });

  // 7.1 / 1.1.60 Copy from Your Lists selection
  window.LIFE_CYCLE_EVENT_BUS.on('add-to.copyFromYourListsSelection', ({ output } = {}) => {
    const flowType = output?.flowType || 'lists';
    const settingsMap = {
      lists: 'lists: list creation: add materials overlay: copy from your lists',
      projects: 'projects: add materials overlay: copy from your lists'
    };
    const ddoEvent = {
      category: {
        primaryCategory: BUTTON_SUBMISSION
      },
      eventInfo: {
        eventName: MY_ACCOUNT_SETTINGS
      },
      myAccount: {
        settings: settingsMap[flowType] || settingsMap.lists
      }
    };

    digitalData.event = digitalData.event || [];
    digitalData.event.push(ddoEvent);
  });

  // 7.3 Copy from Your Lists Exit Overlay
  window.LIFE_CYCLE_EVENT_BUS.on('add-to.copyFromYourListsExitOverlay', () => {
    const ddoEvent = {
      category: {
        primaryCategory: OVERLAY
      },
      eventInfo: {
        eventName: 'copy from your lists: exit confirmation'
      }
    };

    digitalData.event = digitalData.event || [];
    digitalData.event.push(ddoEvent);
  });

  // 7.4 Copy from Your Lists Exit Overlay: Discard and Exit/Cancel
  window.LIFE_CYCLE_EVENT_BUS.on('add-to.copyFromYourListsExitAction', ({ output }) => {
    const actionType = output?.actionType || ''; // 'discard and exit button' or 'cancel button'
    const ddoEvent = {
      category: {
        primaryCategory: BUTTON_SUBMISSION
      },
      eventInfo: {
        eventName: MY_ACCOUNT_SETTINGS
      },
      myAccount: {
        settings: `lists: copy from your lists exit overlay: ${actionType}`
      }
    };

    digitalData.event = digitalData.event || [];
    digitalData.event.push(ddoEvent);
  });

  // 7.5 Mini List Details Overlay
  window.LIFE_CYCLE_EVENT_BUS.on('add-to.miniListDetailsOverlay', () => {
    const ddoEvent = {
      category: {
        primaryCategory: OVERLAY
      },
      eventInfo: {
        eventName: 'mini list details'
      }
    };

    digitalData.event = digitalData.event || [];
    digitalData.event.push(ddoEvent);
  });

  // 6.13.1 Add to List action
  window.LIFE_CYCLE_EVENT_BUS.on('add-to.listAddAction', ({ output }) => {
    const {
      name = 'list add',
      location,
      items,
      isBulkAction,
      listTotal,
      uniqueSkuCount,
      quantityTotal,
      sourceType,
      sessionId,
      starterListName,
      aiInputMethod,
      aiInputType,
      aiPromptLocation = "none",
      projectId,
      projectAddTotal,
      type,
      desc,
      address,
      zip,
      pageOnly
    } = output || {};
    const isProjects = name.includes('project');

    // Return early if required data is missing
    if (!items || !Array.isArray(items)) {
      // Adding a warning log here to help identify if this event is being fired without the necessary data
      console.warn('add-to.listAddAction event missing required items array');
      return;
    }

    // Determine final location string
    let listLocation = getListScreenLocation(location, { pageOnly });
    const itemLocation = isProjects ? 'projectLocation' : 'listLocation';
    const starterKey = isProjects ? 'projectStarter' : 'listStarter';

    // Generate analytics values for intents
    const generateAnalyticsValues = ({
      sourceType,
      sessionId = '',
      starterListName,
      aiInputMethod,
      aiInputType
    }) => {
      let listStarterValue;
      let aiSessionIdValue;

      if (sourceType === 'starter_list') {
        // For starter lists, use the actual starter list name
        listStarterValue = `starter list: ${starterListName}`;
        aiSessionIdValue = ''; // Empty for non-AI sources
      } else if (sourceType === 'ai') {
        // For AI, construct the value based on input method and type
        listStarterValue = `build with ai: ${aiInputMethod}: ${aiInputType}`;
        aiSessionIdValue = sessionId || '';
      } else {
        // Fallback for backward compatibility
        const isFromStarterList = !sessionId;
        listStarterValue = isFromStarterList
          ? 'starter list: customize your list overlay'
          : 'build with ai: customize your list overlay';
        aiSessionIdValue = sessionId || '';
      }

      return { listStarter: listStarterValue, aiSessionId: aiSessionIdValue };
    };

    const itemsFormatted = items.map((item) => {
      // Generate analytics values if context is provided
      let analyticsValues = { listStarter: '', aiSessionId: '' };
      if (sourceType) {
        analyticsValues = generateAnalyticsValues({
          sourceType,
          sessionId,
          starterListName,
          aiInputMethod,
          aiInputType
        });
      }
      const formattedItem = {
        quantity: item.quantity,
        [itemLocation]: listLocation,
        price: {
          basePrice: item.price * item.quantity
        },
        productInfo: {
          sku: item.sku,
          intent: {
            status: item.intentStatus || 'no',
            resolvedIntent: item.resolvedFromIntent || 'no'
          },
          aiSessionId: item.aiSessionId !== undefined ? item.aiSessionId : analyticsValues.aiSessionId,
          aiQuantityEstimate: item.aiQuantityEstimate !== undefined ? item.aiQuantityEstimate : '',
          [starterKey]: item.listStarter || analyticsValues.listStarter
        }
      };
      if (item?.projectLocation) {
        formattedItem.projectLocation = item.projectLocation;
      }
      return formattedItem;
    });

    const ddoEvent = {
      category: {
        primaryCategory: BUTTON_SUBMISSION
      },
      eventInfo: {
        eventName: name,
      },
      item: itemsFormatted
    };
    if (isProjects) {
      ddoEvent.projectId = projectId || '';
      ddoEvent.uniqueSkuCount = uniqueSkuCount || '';
      ddoEvent.projectAddTotal = projectAddTotal || '';
      // 1.1.30 Add project creation settings if this is a new project flow
      if (type !== undefined || desc !== undefined || address !== undefined || zip !== undefined) {
        ddoEvent.myAccount = {
          settings: `projects: project created: type ${type || 'n'}: desc ${desc || 'n'}: address ${address || 'n'}: zip ${zip || 'n'}`
        };
      }
    } else {
      ddoEvent.bulkAction = isBulkAction;
    }

    // Add list-level metrics if provided
    if ((listTotal !== undefined || uniqueSkuCount !== undefined || quantityTotal !== undefined) && !isProjects) {
      ddoEvent.myList = {
        listTotal: listTotal || '',
        uniqueSkuCount: uniqueSkuCount || '',
        quantityTotal: quantityTotal || ''
      };
    }
     
    ddoEvent.myList = {
      ...ddoEvent.myList,
      aiPromptLocation: aiPromptLocation        
    };
    

    digitalData.event = digitalData.event || [];
    digitalData.event.push(ddoEvent);
  });

  // 7.51.59 Select List, Project Overlays
  window.LIFE_CYCLE_EVENT_BUS.on('add-to.copyToSelectListOrProjectOverlays', ({ output }) => {
    const { selectionType } = output || {};
    const ddoEvent = {
      category: {
        primaryCategory: OVERLAY
      },
      eventInfo: {
        eventName: `select ${selectionType}`
      }
    };

    digitalData.event = digitalData.event || [];
    digitalData.event.push(ddoEvent);
  });

  // 7.51.59 (cont.) Select List for the AddToListDrawer component.
  // Sections migrated to add-to-list:AddToListDrawer (Pros Buy These Together,
  // Compare Key Features) never fire add-to.copyToSelectListOrProjectOverlays, so the
  // handler above never produced a "select list" overlay event for them. This maps the
  // drawer's own ready event instead. Two caveats drive the shape of this listener:
  //   - it is only ever triggered on the lifeCycle registry, not the default one
  //   - it fires twice per drawer open, ~1ms apart, hence the guard below
  // The drawer sends no payload, so selectionType cannot be read and "list" is assumed.
  // Remove this once those components emit copyToSelectListOrProjectOverlays themselves.
  let lastDrawerReady = 0;
  window.LIFE_CYCLE_EVENT_BUS.lifeCycle.on('AddToListDrawer.ready', () => {
    const now = Date.now();
    if (now - lastDrawerReady < 500) return;
    lastDrawerReady = now;

    const ddoEvent = {
      category: {
        primaryCategory: OVERLAY
      },
      eventInfo: {
        eventName: 'select list'
      }
    };

    digitalData.event = digitalData.event || [];
    digitalData.event.push(ddoEvent);
  });

  // 7.6 Mini List Details Overlay: Back button
  window.LIFE_CYCLE_EVENT_BUS.on('add-to.miniListDetailsBackButton', () => {
    const ddoEvent = {
      category: {
        primaryCategory: BUTTON_SUBMISSION
      },
      eventInfo: {
        eventName: MY_ACCOUNT_SETTINGS
      },
      myAccount: {
        settings: 'lists: mini list details overlay: back button'
      }
    };

    digitalData.event = digitalData.event || [];
    digitalData.event.push(ddoEvent);
  });

  // 7.7 Mini List Details Exit Overlay
  window.LIFE_CYCLE_EVENT_BUS.on('add-to.miniListDetailsExitOverlay', () => {
    const ddoEvent = {
      category: {
        primaryCategory: OVERLAY
      },
      eventInfo: {
        eventName: 'mini list details: exit confirmation'
      }
    };

    digitalData.event = digitalData.event || [];
    digitalData.event.push(ddoEvent);
  });

  // 7.8 Mini List Details Exit Overlay: Discard and Exit/Cancel
  window.LIFE_CYCLE_EVENT_BUS.on('add-to.miniListDetailsExitAction', ({ output }) => {
    const actionType = output?.actionType || ''; // 'discard and exit button' or 'cancel button'
    const ddoEvent = {
      category: {
        primaryCategory: BUTTON_SUBMISSION
      },
      eventInfo: {
        eventName: MY_ACCOUNT_SETTINGS
      },
      myAccount: {
        settings: `lists: mini list details exit overlay: ${actionType}`
      }
    };

    digitalData.event = digitalData.event || [];
    digitalData.event.push(ddoEvent);
  });

  // 1.1.70 No Projects: Add Items to Project Overlay
  window.LIFE_CYCLE_EVENT_BUS.on('add-to.noProjectsOverlay', () => {
    const ddoEvent = {
      category: {
        primaryCategory: OVERLAY
      },
      eventInfo: {
        eventName: 'no projects: add items to project'
      }
    };

    digitalData.event = digitalData.event || [];
    digitalData.event.push(ddoEvent);
  });

  // 1.1.68 Add Items to Project Overlay Search
  window.LIFE_CYCLE_EVENT_BUS.on('add-to.projectOverlaySearch', () => {
    const ddoEvent = {
      category: {
        primaryCategory: BUTTON_SUBMISSION
      },
      eventInfo: {
        eventName: MY_ACCOUNT_SETTINGS
      },
      myAccount: {
        settings: 'projects: add items to project overlay: existing project search'
      }
    };

    digitalData.event = digitalData.event || [];
    digitalData.event.push(ddoEvent);
  });

  // 1.1.69 Choosing the Right Tool Overlay
  window.LIFE_CYCLE_EVENT_BUS.on('add-to.choosingTheRightToolOverlay', () => {
    const ddoEvent = {
      category: {
        primaryCategory: OVERLAY
      },
      eventInfo: {
        eventName: 'add to: choosing the right tool'
      }
    };

    digitalData.event = digitalData.event || [];
    digitalData.event.push(ddoEvent);
  });

  // 10.1 AI Entry Points - List Builder Entry Point Clicks
  window.LIFE_CYCLE_EVENT_BUS.on('list-builder.entryPointClick', ({ output }) => {
    const entryPoint = output?.entryPoint || '';
    let page = getPageType();
    if (page === 'browse' || page === 'search') page = 'plp';

    // Use dynamic prefix based on page type (lists vs projects)
    const prefixValue = ADD_TO_PREFIX[page];
    const prefix = (prefixValue === 'lists' || prefixValue === 'projects') ? prefixValue : 'other';
    const settings = `${prefix}: add to list overlay: entry point click: ${entryPoint}`;

    const ddoEvent = {
      category: {
        primaryCategory: BUTTON_SUBMISSION
      },
      eventInfo: {
        eventName: MY_ACCOUNT_SETTINGS
      },
      myAccount: {
        settings
      }
    };

    digitalData.event = digitalData.event || [];
    digitalData.event.push(ddoEvent);
  });

  // 10.2 Starter Lists Overlay - Dynamic overlay display tracking
  window.LIFE_CYCLE_EVENT_BUS.on('list-builder.options-overlay', ({ output }) => {
    const overlayType = output?.overlayType || 'unknown';
    let page = getPageType();
    if (page === 'browse' || page === 'search') page = 'plp';

    // Use dynamic prefix based on page type - only lists or projects supported
    const prefixValue = ADD_TO_PREFIX[page];
    const prefix = (prefixValue === 'lists' || prefixValue === 'projects') ? prefixValue : 'other';
    const eventName = `add to ${prefix}: ${overlayType}`;

    const ddoEvent = {
      category: {
        primaryCategory: OVERLAY
      },
      eventInfo: {
        eventName
      }
    };

    digitalData.event = digitalData.event || [];
    digitalData.event.push(ddoEvent);
  });

  // 10.2.1 Starter List Selection
  // Track specific starter list selections and custom entries
  window.LIFE_CYCLE_EVENT_BUS.on('list-builder.starter-list-selection', ({ output }) => {
    const selection = output?.selection || '';
    const aiSessionId = output?.aiSessionId || '';
    const aiTextEntry = output?.aiTextEntry?.substring(0, 255) || '';
    let page = getPageType();
    if (page === 'browse' || page === 'search') page = 'plp';
    // Use dynamic prefix based on page type - only lists or projects supported
    const prefixValue = ADD_TO_PREFIX[page];
    const prefix = (prefixValue === 'lists' || prefixValue === 'projects') ? prefixValue : 'other';
    const settings = `${prefix}: starter lists overlay: ${selection}`;

    const ddoEvent = {
      category: {
        primaryCategory: BUTTON_SUBMISSION
      },
      eventInfo: {
        eventName: MY_ACCOUNT_SETTINGS
      },
      myAccount: {
        settings
      },
      aiSessionId,
      aiTextEntry
    };
    digitalData.event = digitalData.event || [];
    digitalData.event.push(ddoEvent);
  });

  // 10.2.2 10.4.1 Starter List Overlay Exit/Cancel
  // Track when users exit the starter lists overlay
  window.LIFE_CYCLE_EVENT_BUS.on('add-to.overlay-exit', ({ output }) => {
    const screen = output?.screen || 'add to general';

    // Use dynamic prefix based on page type
    const listLocation = getListScreenLocation(screen);
    const settings = `${listLocation} overlay: cancel back or x`;

    const ddoEvent = {
      category: {
        primaryCategory: BUTTON_SUBMISSION
      },
      eventInfo: {
        eventName: MY_ACCOUNT_SETTINGS
      },
      myAccount: {
        settings
      }
    };

    digitalData.event = digitalData.event || [];
    digitalData.event.push(ddoEvent);
  });

  // 10.3 Build Your List with AI Selection
  // Track AI options used to generate a list (Describe Project, Paste List)
  window.LIFE_CYCLE_EVENT_BUS.on('list-builder.build-with-ai-selection', ({ output }) => {
    const aiType = output?.aiType || '';
    const selection = output?.selection || '';
    const aiSessionId = output?.aiSessionId || '';
    const aiTextEntry = output?.aiTextEntry?.substring(0, 255) || '';
    const page = getPageType().toLowerCase();
    const prefix = ( page.includes("projects") ? 'projects' : 'lists' );
    const settings = `${prefix}: build your list with ai: ${aiType}: ${selection}`;

    const ddoEvent = {
      category: {
        primaryCategory: BUTTON_SUBMISSION
      },
      eventInfo: {
        eventName: MY_ACCOUNT_SETTINGS
      },
      myAccount: {
        settings
      },
      myList: {
        aiPromptLocation: digitalData?.page?.pageInfo?.pageType || '' 
      },
      aiSessionId,
      aiTextEntry
    };

    digitalData.event = digitalData.event || [];
    digitalData.event.push(ddoEvent);
  });

  // 10.5 Preview List Overlay
  // Track when customer lands on the Preview List Overlay
  window.LIFE_CYCLE_EVENT_BUS.on('list-builder.customize-list-overlay', ({ output }) => {
    const aiSessionId = output?.aiSessionId || '';

    const eventName = LIST_BUILDER_OVERLAY_PREVIEW_LIST; // Use constant for consistency

    const ddoEvent = {
      category: {
        primaryCategory: OVERLAY
      },
      eventInfo: {
        eventName
      },
      aiSessionId,
    };

    digitalData.event = digitalData.event || [];
    digitalData.event.push(ddoEvent);
  });

  // 10.5.1 Preview List Overlay: Fork Selection
  // Track when user selects "I'll choose the products" or "Choose for me" options
  window.LIFE_CYCLE_EVENT_BUS.on('list-builder.preview-list-fork-selection', ({ output }) => {
    let page = getPageType();
    if (page === 'browse' || page === 'search') page = 'plp';
    const currentPageName = page === 'project details' ? 'projects' : 'lists'
    const selection = output?.selection || '';

    // Map selection to settings string
    const settingsMap = {
      'ill-choose': `${currentPageName}: preview list overlay: ill choose the products select`,
      'choose-for-me': `${currentPageName}: preview list overlay: choose for me select`
    };

    const settings = settingsMap[selection] || '';

    const ddoEvent = {
      category: {
        primaryCategory: BUTTON_SUBMISSION
      },
      eventInfo: {
        eventName: MY_ACCOUNT_SETTINGS
      },
      myAccount: {
        settings
      }
    };

    digitalData.event = digitalData.event || [];
    digitalData.event.push(ddoEvent);
  });

  window.LIFE_CYCLE_EVENT_BUS.on('list-builder.qna-ai-overlay', ({ output }) => {

    const eventName = "lbai q and a";
    const aiSessionId = output?.aiSessionId || '';

    const ddoEvent = {
      category: {
        primaryCategory: OVERLAY
      },
      eventInfo: {
        eventName
      },
      aiSessionId
    };

    digitalData.event = digitalData.event || [];
    digitalData.event.push(ddoEvent);

  });

    window.LIFE_CYCLE_EVENT_BUS.on('list-builder.qna-ai-view', ({ output }) => {
    
    const questionCount = output?.questionCount || 0;
   
    const aiSessionId = output?.aiSessionId || '';
   
    const settings = `lists: q and a overlay: pageload: ${questionCount}`;

    const ddoEvent = {
      category: {
        primaryCategory: BUTTON_SUBMISSION
      },
      eventInfo: {
        eventName: MY_ACCOUNT_SETTINGS
      },
      myAccount: {
        settings: settings
      },
      aiSessionId
    };
    digitalData.event = digitalData.event || [];
    digitalData.event.push(ddoEvent);

  });

     window.LIFE_CYCLE_EVENT_BUS.on('list-builder.qna-ai-click', ({ output }) => {
    
    const uiType = (Array.isArray(output?.uiType) ? output.uiType.join(', ') : output?.uiType || '')  ;
    const questionCount = output?.questionCount || 0;
    const aiSessionId = output?.aiSessionId || '';
   
    const settings = `lists: q and a overlay: pageload: ${uiType}: ${questionCount}`;

    const ddoEvent = {
      category: {
        primaryCategory: BUTTON_SUBMISSION
      },
      eventInfo: {
        eventName: MY_ACCOUNT_SETTINGS
      },
      myAccount: {
        settings: settings
      },
      aiSessionId
    };
    digitalData.event = digitalData.event || [];
    digitalData.event.push(ddoEvent);

  });

    window.LIFE_CYCLE_EVENT_BUS.on('list-builder.qna-ai-continue', ({ output }) => {

    const button = output?.button || '';
    const aiSessionId = output?.aiSessionId || '';
    const settings = `lists: q and a overlay: ${button} button${button === 'continue' ? ': no changes' : ''}`;

    const ddoEvent = {
      category: {
        primaryCategory: BUTTON_SUBMISSION
      },
      eventInfo: {
        eventName: MY_ACCOUNT_SETTINGS
      },
      myAccount: {
        settings
      },
      aiSessionId
    };
    digitalData.event = digitalData.event || [];
    digitalData.event.push(ddoEvent);

  });


};
